import { zodResolver } from '@hookform/resolvers/zod';
import { G } from '@mobily/ts-belt';
import { Box, Stack } from '@mui/material';
import { styled } from '@mui/material/styles';
import { useState } from 'react';
import { useNotify, useRedirect, useTranslate } from 'react-admin';
import { useForm } from 'react-hook-form';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { match } from 'ts-pattern';

import {
  PostChangePasswordRequest,
  ZodPostChangePasswordRequest,
} from '@Common/types/bo_external/auth/request_email';
import { isUnsupportedPhoneNumber } from '@Common/types/zod/zod-phone-number';

import { TypographyTranslated } from '@sh4res/bo-common/components';
import { CustomLocalesMenuButton } from '@sh4res/bo-common/components/CustomLocalesMenuButton';
import { PageTitle } from '@sh4res/bo-common/components/PageTitle';
import { PasswordInput } from '@sh4res/bo-common/fields';

import { AxaTypography } from '@Components/AxaTypography';
import { LoginPageCTA } from '@Components/LoginPageCTA';
import { VerifyOtpPageForm } from '@Components/VerifyOtpPageForm';
import { TranslationKeys } from '@I18n/types';
import { authClient } from '@Utils/authClient';

function getPasswordResetOtpType(
  alternateEmail: string | null,
  phoneNumber: string | null,
): 'email' | 'sms' | 'support' {
  if (
    alternateEmail &&
    phoneNumber &&
    phoneNumber !== 'null' &&
    !isUnsupportedPhoneNumber(phoneNumber)
  ) {
    return 'sms';
  }
  if (alternateEmail) {
    return 'email';
  }
  if (phoneNumber === 'null') {
    return 'support';
  }
  return 'sms';
}

const ChangePasswordPageFormWrapper = styled('form')`
  text-align: center;
  display: flex;
  flex-direction: column;
  min-width: 290px;
`;

function ChangePasswordForm({
  token,
  showLanguagePicker,
}: {
  token: string;
  showLanguagePicker: boolean;
}) {
  const translate = useTranslate();
  const testId = 'BoExternalChangePasswordForm';
  const redirect = useRedirect();
  const notify = useNotify();

  const {
    register,
    handleSubmit,
    formState: { errors, isValid },
  } = useForm({ mode: 'onBlur', resolver: zodResolver(ZodPostChangePasswordRequest) });

  const [loading, setLoading] = useState(false);

  const onSubmit = async (formData: unknown) => {
    setLoading(true);
    const { password } = formData as PostChangePasswordRequest;
    await authClient.resetPassword(
      {
        newPassword: password,
        token,
      },
      {
        onResponse() {
          setLoading(false);
        },
        onSuccess() {
          redirect('/');
        },
        onError({ error }) {
          // oxlint-disable-next-line no-console
          // eslint-disable-next-line no-console
          console.error(error);

          let errorTranslationKey: TranslationKeys = 'login_page.messages.change_password.failed';

          if (
            'code' in error &&
            typeof error.code === 'string' &&
            error.code.startsWith('WEAK_PASSWORD')
          ) {
            errorTranslationKey = match(error.code)
              .with(
                'WEAK_PASSWORD_HAS_WORDS',
                () => 'login_page.messages.change_password.weak_password_has_words' as const,
              )
              .with(
                'WEAK_PASSWORD_HAS_CONSECUTIVE_CHARS',
                () =>
                  'login_page.messages.change_password.weak_password_has_consecutive_chars' as const,
              )
              .with(
                'WEAK_PASSWORD',
                () => 'login_page.messages.change_password.weak_password' as const,
              )
              .otherwise(() => errorTranslationKey);
          }
          notify(errorTranslationKey, { type: 'error' });
        },
      },
    );
  };

  const rawHeaderText = 'login_page.messages.change_password.header';

  return (
    <>
      <PageTitle title={translate(rawHeaderText)} />
      <ChangePasswordPageFormWrapper onSubmit={handleSubmit(onSubmit)}>
        {showLanguagePicker && (
          <Box position="absolute" top={24} right={24}>
            <CustomLocalesMenuButton testId={`${testId}_LocalesMenuButton`} />
          </Box>
        )}
        <TypographyTranslated
          label={rawHeaderText}
          component={AxaTypography}
          variant="h5"
          sx={{ mt: '40px' }}
          align="left"
        />
        <TypographyTranslated
          label="login_page.messages.change_password.subheader"
          variant="body2"
          sx={{ mb: '16px', mt: 0 }}
          align="left"
        />
        <PasswordInput
          testId={`${testId}_NewPasswordInput`}
          label={translate('login_page.messages.form.password_placeholder')}
          variant="outlined"
          sx={{
            borderRadius: '16px',
            mb: '24px',
            width: '100%',
          }}
          defaultValue=""
          error={!!errors.password}
          helperText={
            G.isNotNullable(errors.password?.message)
              ? translate(errors.password.message as TranslationKeys)
              : undefined
          }
          {...register('password')}
        />
        <Box sx={{ maxWidth: '500px' }}>
          <TypographyTranslated
            label="login_page.messages.change_password.description.header"
            variant="body1"
            textAlign="left"
          />
          <ul>
            <li>
              <TypographyTranslated
                label="login_page.messages.change_password.description.line1"
                variant="body1"
                textAlign="left"
              />
            </li>
            <li>
              <TypographyTranslated
                label="login_page.messages.change_password.description.line2"
                variant="body1"
                textAlign="left"
              />
            </li>
            <li>
              <TypographyTranslated
                label="login_page.messages.change_password.description.line3"
                variant="body1"
                textAlign="left"
              />
            </li>
          </ul>
        </Box>
        <PasswordInput
          testId={`${testId}_ConfirmPasswordInput`}
          label={translate('login_page.messages.form.confirm_password_placeholder')}
          variant="outlined"
          sx={{
            borderRadius: '16px',
            mb: '24px',
            width: '100%',
          }}
          defaultValue=""
          error={!!errors.confirm_password}
          helperText={
            G.isNotNullable(errors.confirm_password?.message)
              ? translate(errors.confirm_password.message as TranslationKeys)
              : undefined
          }
          {...register('confirm_password')}
        />
        <Stack alignItems="end">
          <LoginPageCTA
            data-testid={`${testId}_SubmitButton`}
            type="submit"
            variant="contained"
            disabled={!isValid || loading}
            sx={{ width: 'max-content' }}>
            <TypographyTranslated
              component={AxaTypography}
              label="login_page.messages.change_password.cta_text"
            />
          </LoginPageCTA>
        </Stack>
      </ChangePasswordPageFormWrapper>
    </>
  );
}

export function ChangePasswordPageForm() {
  const navigate = useNavigate();
  const notify = useNotify();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const phoneNumber = searchParams.get('phoneNumber');
  const alternateEmail = searchParams.get('alternateEmail');

  const [twoFactor, setTwoFactor] = useState<boolean>(!!phoneNumber || !!alternateEmail);

  if (!token) {
    return <Navigate to="/" replace />;
  }

  if (twoFactor) {
    return (
      <VerifyOtpPageForm
        showLanguagePicker={false}
        token={token}
        autoSend
        type={getPasswordResetOtpType(alternateEmail, phoneNumber)}
        alternateEmail={alternateEmail ?? undefined}
        send={async () => authClient.passwordReset.sendOtp({ token })}
        verify={async (code, fetchOptions) =>
          authClient.passwordReset.verify({ code, token }, fetchOptions)
        }
        onSuccess={() => {
          setTwoFactor(false);
        }}
        onExpired={() => {
          navigate('/login', { replace: true });
          notify('login_page.messages.change_password.disabled', { type: 'error' });
        }}
      />
    );
  }

  return <ChangePasswordForm token={token} showLanguagePicker={!phoneNumber && !alternateEmail} />;
}
